import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import { CrewList, TableColumn, CrewChange } from '../../types';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import QuickCrewList from '../../components/HR/QuickCrewList';
import { exportToExcel } from '../../utils/exportToExcel';
import { PageHeader, StatsCards } from '../../components/Common';
import type { StatCard } from '../../components/Common';
import ActiveCrewInfoBanner from './ActiveCrewInfoBanner';
import ActiveCrewFilters from './ActiveCrewFilters';
import { generatePrintHtml } from './ActiveCrewPrintTemplate';

const ActiveCrewListArabic: React.FC = () => {
  const { data: employees = [] } = useEmployees({ populate: '*' });
  const { data: branches = [] } = useBranches({ populate: '*' });
  const [showQuickList, setShowQuickList] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [rankFilter, setRankFilter] = useState<string>('all');

  const [crewChanges] = useState<CrewChange[]>([
    { id: '1', employeeId: '1', status: 'sign_on', changeDate: '2024-01-15', branchId: '1', raftTaken: true, takenTaken: false, notes: 'Regular rotation', createdAt: '2024-01-15T10:00:00Z' },
    { id: '2', employeeId: '2', status: 'continue', changeDate: '2024-01-20', branchId: '2', raftTaken: false, takenTaken: true, notes: 'Extended contract', createdAt: '2024-01-20T14:30:00Z' },
    { id: '3', employeeId: '3', status: 'sign_off', changeDate: '2024-01-25', branchId: '3', raftTaken: false, takenTaken: false, notes: 'End of contract', createdAt: '2024-01-25T09:15:00Z' },
    { id: '4', employeeId: '1', status: 'continue', changeDate: '2024-02-01', branchId: '1', raftTaken: false, takenTaken: false, notes: 'Contract extension', createdAt: '2024-02-01T08:00:00Z' }
  ]);

  const [activeCrewList, setActiveCrewList] = useState<CrewList[]>([]);

  const findEmployee = useCallback((employeeId: string) => {
    return employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
  }, [employees]);

  const getEmployeeName = useCallback((employeeId: string) => findEmployee(employeeId)?.name || '-', [findEmployee]);
  const getEmployeeRank = useCallback((employeeId: string) => {
    const emp = findEmployee(employeeId);
    return emp ? (emp.rank || emp.position || '-') : '-';
  }, [findEmployee]);
  const getEmployeeNationality = useCallback((employeeId: string) => findEmployee(employeeId)?.nationality || '-', [findEmployee]);
  const getEmployeeBirthDate = useCallback((employeeId: string) => {
    const emp = findEmployee(employeeId);
    return emp?.birthDate ? new Date(emp.birthDate).toLocaleDateString('ar-EG') : '-';
  }, [findEmployee]);
  const getEmployeeSeamanBookNumber = useCallback((employeeId: string) => findEmployee(employeeId)?.seamanBookNumber || '-', [findEmployee]);
  const getEmployeeSeamanBookExpiry = useCallback((employeeId: string) => {
    const emp = findEmployee(employeeId);
    return emp?.seamanBookExpiry ? new Date(emp.seamanBookExpiry).toLocaleDateString('ar-EG') : '-';
  }, [findEmployee]);

  const getBranchName = useCallback((branchId: string) => {
    const branch = branches.find((b: any) => b.id?.toString() === branchId || b.documentId === branchId);
    return branch ? branch.name : '-';
  }, [branches]);

  const generateCrewList = useCallback(() => {
    const employeeLatestChanges: { [key: string]: CrewChange } = {};
    crewChanges.forEach(change => {
      const existing = employeeLatestChanges[change.employeeId!];
      if (!existing || new Date(change.createdAt!) > new Date(existing.createdAt!)) {
        employeeLatestChanges[change.employeeId!] = change;
      }
    });
    const activeChanges = Object.values(employeeLatestChanges).filter(
      change => change.status === 'sign_on' || change.status === 'continue'
    );
    const crewList: CrewList[] = activeChanges.map(change => ({
      id: `crew_${change.employeeId!}`,
      employeeId: change.employeeId!,
      joinDate: change.changeDate,
      branchId: change.branchId!,
      status: 'active'
    }));
    setActiveCrewList(crewList);
  }, [crewChanges]);

  useEffect(() => { generateCrewList(); }, [generateCrewList]);

  const uniqueBranches = Array.from(new Set(activeCrewList.map(crew => crew.branchId).filter(Boolean)));
  const uniqueRanks = Array.from(new Set(activeCrewList.map(crew => getEmployeeRank(crew.employeeId)).filter(Boolean)));

  const filteredActiveCrewList = useMemo(() => {
    return activeCrewList.filter(crew => {
      const matchesSearch = !searchTerm || (
        getEmployeeName(crew.employeeId).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getBranchName(crew.branchId).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getEmployeeRank(crew.employeeId).toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || crew.status === statusFilter;
      const matchesBranch = branchFilter === 'all' || crew.branchId === branchFilter;
      const matchesRank = rankFilter === 'all' || getEmployeeRank(crew.employeeId) === rankFilter;
      return matchesSearch && matchesStatus && matchesBranch && matchesRank;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCrewList, searchTerm, statusFilter, branchFilter, rankFilter]);

  const handleExport = () => {
    const exportData = filteredActiveCrewList.map(crew => ({
      'اسم الموظف': getEmployeeName(crew.employeeId),
      'الرتبة': getEmployeeRank(crew.employeeId),
      'السفينة/المقر': getBranchName(crew.branchId),
      'الجنسية': getEmployeeNationality(crew.employeeId),
      'تاريخ الميلاد': getEmployeeBirthDate(crew.employeeId),
      'رقم الجواز البحري': getEmployeeSeamanBookNumber(crew.employeeId),
      'انتهاء الجواز البحري': getEmployeeSeamanBookExpiry(crew.employeeId),
      'تاريخ الانضمام': new Date(crew.joinDate).toLocaleDateString('ar-EG'),
      'الحالة': crew.status === 'active' ? 'نشط' : crew.status
    }));
    exportToExcel(exportData, `active_crew_list_arabic_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const crewData = filteredActiveCrewList.map(crew => ({
      name: getEmployeeName(crew.employeeId),
      rank: getEmployeeRank(crew.employeeId),
      nationality: getEmployeeNationality(crew.employeeId),
      dateOfBirth: getEmployeeBirthDate(crew.employeeId),
      seamanBookNo: getEmployeeSeamanBookNumber(crew.employeeId),
      seamanBookExpiry: getEmployeeSeamanBookExpiry(crew.employeeId),
      dateOfJoin: new Date(crew.joinDate).toLocaleDateString('ar-EG')
    }));
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(generatePrintHtml(crewData));
      printWindow.document.close();
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBranchFilter('all');
    setRankFilter('all');
  };

  const columns: TableColumn[] = [
    { key: 'employeeId', label: 'اسم الموظف', sortable: true, render: (_: string, item: CrewList) => getEmployeeName(item.employeeId!) },
    { key: 'employeeId', label: 'الرتبة', sortable: true, render: (_: string, item: CrewList) => getEmployeeRank(item.employeeId!) },
    { key: 'branchId', label: 'السفينة/المقر', sortable: true, render: (_: string, item: CrewList) => getBranchName(item.branchId!) },
    { key: 'employeeId', label: 'الجنسية', sortable: true, render: (_: string, item: CrewList) => getEmployeeNationality(item.employeeId!) },
    { key: 'employeeId', label: 'تاريخ الميلاد', sortable: true, render: (_: string, item: CrewList) => getEmployeeBirthDate(item.employeeId!) },
    { key: 'employeeId', label: 'رقم الجواز البحري', sortable: true, render: (_: string, item: CrewList) => getEmployeeSeamanBookNumber(item.employeeId!) },
    { key: 'employeeId', label: 'انتهاء الجواز البحري', sortable: true, render: (_: string, item: CrewList) => getEmployeeSeamanBookExpiry(item.employeeId!) },
    { key: 'joinDate', label: 'تاريخ الانضمام', sortable: true, render: (value: string) => new Date(value).toLocaleDateString('ar-EG') },
    { key: 'status', label: 'الحالة', sortable: true, render: (value: string) => (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        {value === 'active' ? 'نشط' : value}
      </span>
    )}
  ];

  const stats: StatCard[] = [
    { label: 'إجمالي الطاقم النشط', value: activeCrewList.length, color: 'green' },
    { label: 'الفروع النشطة', value: new Set(activeCrewList.map(crew => crew.branchId)).size, color: 'blue' },
    { label: 'الرتب الفريدة', value: uniqueRanks.length, color: 'yellow' },
    { label: 'آخر تحديث', value: new Date().toLocaleDateString('ar-EG'), color: 'purple' },
  ];

  return (
    <div className="space-y-6" dir="rtl">
      <PageHeader
        title="قائمة الطاقم النشط"
        description="أعضاء الطاقم الحاليون الذين تم تسجيلهم أو يواصلون مهامهم"
        actions={[{ label: 'تحديث القائمة', onClick: generateCrewList, variant: 'primary' }]}
      />

      <StatsCards stats={stats} />

      <ActiveCrewInfoBanner onQuickPrint={() => setShowQuickList(true)} />

      <ActiveCrewFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        branchFilter={branchFilter}
        onBranchFilterChange={setBranchFilter}
        rankFilter={rankFilter}
        onRankFilterChange={setRankFilter}
        uniqueBranches={uniqueBranches}
        uniqueRanks={uniqueRanks}
        getBranchName={getBranchName}
        filteredCount={filteredActiveCrewList.length}
        totalCount={activeCrewList.length}
        onExport={handleExport}
        onPrint={handlePrint}
        onClearFilters={handleClearFilters}
      />

      <Table
        columns={columns}
        data={filteredActiveCrewList}
        showAddButton={false}
        showEditButton={false}
        showDeleteButton={false}
        searchable={false}
      />

      {showQuickList && (
        <QuickCrewList onClose={() => setShowQuickList(false)} />
      )}
    </div>
  );
};

export default ActiveCrewListArabic;
